import { createContainer } from './app/container.js';
import { registerComponents } from './components/register.js';
import { mountShell } from './app/shell.js';
import { Router } from './app/router.js';
import { wireCommands } from './app/wire-commands.js';
import { installErrorBoundary } from './app/error-boundary.js';
import { applyDocumentLocale } from './i18n/locale.js';

/**
 * Composition + bootstrap entry point for the People Engine.
 *
 * Wires the container (mock-always), registers components, mounts the shell
 * (title + SIMULATION badge), installs the global error boundary, and starts
 * the lazy-route router at the Attention (home) view.
 */
function bootstrap(): void {
  const root = document.getElementById('app');
  if (!root) throw new Error('Missing #app root element');

  // import.meta.env is Vite's typed env bag; cast to our RawEnv shape.
  // `?lang=` overrides the build-time LOCALE at the boot edge (parse, don't
  // validate: only known values pass) so the shell toggle can switch language
  // with a clean reload — no reactive message plumbing (P8).
  const env = import.meta.env as Record<string, string | boolean | undefined>;
  const lang = new URLSearchParams(window.location.search).get('lang');
  const container = createContainer(
    lang === 'ar' ? { ...env, LOCALE: 'ar-SA' } : lang === 'en' ? { ...env, LOCALE: 'en-US' } : env,
  );

  // Reflect locale + direction on <html> (RTL-ready) from typed config.
  applyDocumentLocale(document, container.config.locale);

  registerComponents();

  installErrorBoundary(container.telemetry, () => {
    root.replaceChildren();
    const p = document.createElement('p');
    p.className = 'view-status';
    p.textContent = container.messages.errorGeneric;
    root.appendChild(p);
  });

  const outlet = mountShell(root, container);

  const router = new Router(container, outlet)
    .add('/', () => import('./views/attention.js'))
    .add('/people', () => import('./views/people.js'))
    .add('/people/:id', () => import('./views/person.js'))
    .add('/cases', () => import('./views/cases.js'))
    .add('/cases/:id', () => import('./views/case-detail.js'));

  // Semantic commands → effects (ADR 0032).
  wireCommands(container, router);

  // The watchdog runs at boot: expiring documents open their renewal cases
  // before the operator even looks (the SOPs "open themselves" — P6).
  container.commands.dispatch('compliance.scan', {});

  router.start();
}

bootstrap();
